// Fixed-size orientation gizmo in the bottom-left corner (Inventor-style).
// Position and size stay constant; orientation tracks the main camera.
import * as THREE from 'three';
import { chromeScale } from './ui-scale';
import { camera, onAfterRender, renderer } from './viewer';

const GIZMO_CSS_BASE = 64;
const AXIS_LEN = 1.0;
/** Must match the phone breakpoint in styles.css. */
const PHONE_MAX_WIDTH = 720;

/**
 * On phones the tool dock sits in the bottom-left corner the gizmo would use.
 * Measuring it (instead of hard-coding a height) keeps the two apart whatever
 * the safe-area inset turns out to be on the device.
 */
let dockClearanceCss: number | null = null;

function measureDockClearance(): number {
  if (window.innerWidth > PHONE_MAX_WIDTH) return 0;
  const dock = document.querySelector('.btn-row.toolbar');
  if (!dock) return 0;
  const rect = dock.getBoundingClientRect();
  // Only when the dock really is the bottom bar, not the top toolbar.
  if (rect.height === 0 || rect.top < window.innerHeight * 0.5) return 0;
  return window.innerHeight - rect.top + 8;
}

function dockClearance(): number {
  if (dockClearanceCss === null) dockClearanceCss = measureDockClearance();
  return dockClearanceCss;
}

const gizmoScene = new THREE.Scene();
const gizmoCamera = new THREE.OrthographicCamera(-1.6, 1.6, 1.6, -1.6, 0.1, 10);
gizmoCamera.position.set(0, 0, 4);
gizmoCamera.lookAt(0, 0, 0);

const gizmoRoot = new THREE.Group();
gizmoScene.add(gizmoRoot);

let registered = false;

function makeAxis(dir: THREE.Vector3, color: number, label: string): THREE.Group {
  const group = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({ color, depthTest: true, depthWrite: true, toneMapped: false });
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, AXIS_LEN * 0.72, 10), mat);
  shaft.position.copy(dir.clone().multiplyScalar(AXIS_LEN * 0.36));
  shaft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  const cone = new THREE.Mesh(new THREE.ConeGeometry(0.1, AXIS_LEN * 0.28, 12), mat);
  cone.position.copy(dir.clone().multiplyScalar(AXIS_LEN * 0.86));
  cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  group.add(shaft, cone);

  // Simple sprite-like label via a small plane with canvas texture
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, 64, 64);
  ctx.font = 'bold 42px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#' + color.toString(16).padStart(6, '0');
  ctx.fillText(label, 32, 34);
  const tex = new THREE.CanvasTexture(canvas);
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false, toneMapped: false }));
  sprite.scale.set(0.45, 0.45, 1);
  sprite.position.copy(dir.clone().multiplyScalar(AXIS_LEN * 1.2));
  group.add(sprite);
  return group;
}

function buildGizmo(): void {
  gizmoRoot.clear();
  gizmoRoot.add(makeAxis(new THREE.Vector3(1, 0, 0), 0xe74c3c, 'X'));
  gizmoRoot.add(makeAxis(new THREE.Vector3(0, 1, 0), 0x2ecc71, 'Y'));
  gizmoRoot.add(makeAxis(new THREE.Vector3(0, 0, 1), 0x3498db, 'Z'));
}

function renderGizmo(): void {
  // Match world axes orientation as seen by the main camera.
  gizmoRoot.quaternion.copy(camera.quaternion).invert();

  // setViewport/setScissor take CSS pixels — three.js applies the renderer's
  // pixel ratio itself. Multiplying by it here put the gizmo at twice the
  // offset and twice the size on every 2x screen (phones), while a 1x desktop
  // looked right, which is why it went unnoticed.
  const size = GIZMO_CSS_BASE * chromeScale();
  const margin = 12 * chromeScale();
  const x = margin;
  const y = margin + dockClearance();

  const prevAutoClear = renderer.autoClear;
  const prevVp = new THREE.Vector4();
  const prevSc = new THREE.Vector4();
  renderer.getViewport(prevVp);
  renderer.getScissor(prevSc);
  const prevScissorTest = renderer.getScissorTest();

  renderer.autoClear = false;
  renderer.clearDepth();
  renderer.setScissorTest(true);
  renderer.setViewport(x, y, size, size);
  renderer.setScissor(x, y, size, size);
  renderer.render(gizmoScene, gizmoCamera);

  renderer.setViewport(prevVp);
  renderer.setScissor(prevSc);
  renderer.setScissorTest(prevScissorTest);
  renderer.autoClear = prevAutoClear;
}

/** Register the corner orientation gizmo (idempotent). */
export function initAxisGizmo(): void {
  if (registered) return;
  registered = true;
  buildGizmo();
  // Re-measure on resize / rotation instead of per frame: a getBoundingClientRect
  // in the render loop would force a layout every frame.
  window.addEventListener('resize', () => { dockClearanceCss = null; });
  onAfterRender(renderGizmo);
}
